import { useEffect, useState } from "react";

// Shared narrow-width state for the AI Assistant. Module-level so the
// user's choice (full width vs ~3/4 width) survives page navigations
// within the SPA, and mirrored to localStorage so a refresh or a new
// test continues with whatever the user last picked.
//
// Default: true (= narrow). New users start with the slimmer assistant
// so the question content has more room by default.

const STORAGE_KEY = "testcraft.chat.narrowWidth";
const DEFAULT_VALUE = true;

function readInitial(): boolean {
  try {
    if (typeof window === "undefined") return DEFAULT_VALUE;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
    return DEFAULT_VALUE;
  } catch {
    return DEFAULT_VALUE;
  }
}

let sharedNarrowWidth = readInitial();
const listeners = new Set<(value: boolean) => void>();

function setShared(next: boolean) {
  sharedNarrowWidth = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
  } catch {
    /* private mode */
  }
  listeners.forEach((listener) => listener(next));
}

/** Reset to the default (true). Called from TestContext.resetTest() so a
 *  new test starts narrow regardless of the user's prior session pick. */
export function resetChatNarrowWidth() {
  setShared(DEFAULT_VALUE);
}

export function useChatNarrowWidth(): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState(sharedNarrowWidth);

  useEffect(() => {
    listeners.add(setValue);
    setValue(sharedNarrowWidth);
    return () => {
      listeners.delete(setValue);
    };
  }, []);

  return [value, setShared];
}
